import api, { getApiErrors } from '@app/services/api';
import type {
    ApiError,
    LocationAddParams,
    LocationAddResponse,
    LocationDeleteParams,
    LocationDeleteResponse,
    LocationEditParams,
    LocationEditResponse,
    LocationListParams,
} from '@app/services/api.types';
import { fn } from '@app/utils';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';

export const createLocation = async (params: LocationAddParams): Promise<LocationAddResponse> => {
    const url = apiEndpoints.locations.add;
    const response = await api.post(url, params);
    return response?.data?.data;
};

export const updateLocation = async (params: LocationEditParams): Promise<LocationEditResponse> => {
    const url = apiEndpoints.locations.update(params.id);
    const response = await api.put(url, params);
    return response?.data?.data;
};

export const removeLocation = async (
    params: LocationDeleteParams
): Promise<LocationDeleteResponse> => {
    const url = apiEndpoints.locations.delete(params.id);
    const response = await api.delete(url);
    return response?.data?.data;
};

export const exportLocationsList = async (params: LocationListParams) => {
    const url = apiEndpoints.locations.getListExport(params);
    const response = await api.get<Blob>(url, {
        responseType: 'blob',
    });
    return response;
};

const useLocationActions = () => {
    const { message } = useApp();

    const addLocation = useMutation<LocationAddResponse, AxiosError<ApiError>, LocationAddParams>({
        mutationFn: createLocation,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Location added successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = response?.message || defaultErrorMessage;
            message.error({
                type: 'error',
                content: errorMessage,
            });
        },
        meta: {
            invalidates: [['location', 'list']],
        },
    });

    const editLocation = useMutation<
        LocationEditResponse,
        AxiosError<ApiError>,
        LocationEditParams
    >({
        mutationFn: updateLocation,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Location updated successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = response?.message || defaultErrorMessage;
            message.error({
                type: 'error',
                content: errorMessage,
            });
        },
        meta: {
            invalidates: [
                ['location', 'list'],
                ['location', 'single'],
            ],
        },
    });

    const deleteLocation = useMutation<
        LocationDeleteResponse,
        AxiosError<ApiError>,
        LocationDeleteParams
    >({
        mutationFn: removeLocation,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Location deleted successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['location', 'list']],
        },
    });

    const exportLocations = useMutation<
        AxiosResponse<Blob>,
        AxiosError<ApiError>,
        LocationListParams
    >({
        mutationFn: exportLocationsList,
        onSuccess: (res) => {
            const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
            const filename = match?.[1] || 'locations.csv';
            fn.downloadFileFromBlob({
                blob: res.data,
                fileName: filename,
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    return {
        addLocation,
        editLocation,
        deleteLocation,
        exportLocations,
    };
};

export default useLocationActions;
